import React, { useCallback, useEffect, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import Layout from './Layout';
import {
  annulerInvitation, inviter, listerAcces, listerIntervenants,
  listerInvitations, revoquerAcces,
} from './api';

const NIVEAUX = [
  { code: 'LECTURE', libelle: 'Lecture seule',
    aide: "Il voit le dossier, il ne le modifie pas." },
  { code: 'GESTION', libelle: 'Lecture et modification',
    aide: "Il peut déclarer, chiffrer, déposer des pièces." },
];

const dateCourte = (v) =>
  v ? new Date(v).toLocaleDateString('fr-BE') : '';

/**
 * Le partage d'un dossier.
 *
 * L'assuré et son intermédiaire travaillent sur UN dossier, pas sur deux
 * copies : deux versions d'une même réclamation qui divergent produisent un
 * dossier qui se contredit devant l'assureur.
 */
export default function Partage({ expertiseId, onRetour }) {
  const [acces, setAcces] = useState(null);
  const [invitations, setInvitations] = useState([]);
  const [intervenants, setIntervenants] = useState([]);
  const [erreur, setErreur] = useState('');
  const [formulaire, setFormulaire] = useState(null);
  const [occupe, setOccupe] = useState(false);

  const recharger = useCallback(() => {
    Promise.all([
      listerAcces(expertiseId),
      listerInvitations(expertiseId),
      listerIntervenants(expertiseId).catch(() => []),
    ])
      .then(([a, i, p]) => { setAcces(a); setInvitations(i); setIntervenants(p); })
      .catch(() => setErreur('Impossible de charger les accès.'));
  }, [expertiseId]);

  useEffect(() => { recharger(); }, [recharger]);

  const envoyer = async () => {
    setOccupe(true);
    setErreur('');
    try {
      await inviter(expertiseId, formulaire);
      setFormulaire(null);
      recharger();
    } catch (e) {
      setErreur(e?.response?.data?.detail
        || e?.response?.data?.email?.[0]
        || "L'invitation n'a pas pu être envoyée.");
    } finally {
      setOccupe(false);
    }
  };

  const enAttente = invitations.filter((i) => i.statut === 'en attente');
  const actifs = (acces || []).filter((a) => !a.revoked_at);

  return (
    <Layout
      titre="Qui a accès à ce dossier"
      introduction="Votre courtier, votre conjoint, un proche qui vous aide. Vous partagez un seul dossier : personne ne travaille sur une copie qui divergerait de la vôtre."
      actions={<Button onClick={onRetour}>Retour au dossier</Button>}
    >
      {erreur && <Alert severity="error" sx={{ mb: 3 }}>{erreur}</Alert>}
      {acces === null && !erreur && <CircularProgress size={28} />}

      <Stack spacing={3}>
        {actifs.length > 0 && (
          <Box>
            <Typography variant="h3" sx={{ mb: 1.5 }}>Accès en cours</Typography>
            <Stack spacing={1.5}>
              {actifs.map((a) => (
                <Paper key={a.id} variant="outlined" sx={{ p: 2.5 }}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}
                         alignItems={{ sm: 'center' }}
                         justifyContent="space-between">
                    <Box>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        {a.organization_name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {NIVEAUX.find((n) => n.code === a.level)?.libelle || a.level}
                        {a.granted_at && ` · depuis le ${dateCourte(a.granted_at)}`}
                      </Typography>
                    </Box>
                    <Button color="inherit"
                            onClick={async () => {
                              await revoquerAcces(expertiseId, a.id);
                              recharger();
                            }}>
                      Retirer l'accès
                    </Button>
                  </Stack>
                </Paper>
              ))}
            </Stack>
          </Box>
        )}

        {enAttente.length > 0 && (
          <Box>
            <Typography variant="h3" sx={{ mb: 1.5 }}>
              Invitations envoyées
            </Typography>
            <Stack spacing={1.5}>
              {enAttente.map((i) => (
                <Paper key={i.id} variant="outlined" sx={{ p: 2.5 }}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}
                         alignItems={{ sm: 'center' }}
                         justifyContent="space-between">
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        {i.email}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {NIVEAUX.find((n) => n.code === i.level)?.libelle}
                        {i.expires_at && ` · expire le ${dateCourte(i.expires_at)}`}
                      </Typography>
                      {i.lien && (
                        <Typography variant="caption" color="text.secondary"
                                    sx={{ wordBreak: 'break-all' }}>
                          {i.lien}
                        </Typography>
                      )}
                    </Box>
                    <Stack direction="row" spacing={1}>
                      {i.lien && (
                        <Button onClick={() => navigator.clipboard?.writeText(i.lien)}>
                          Copier le lien
                        </Button>
                      )}
                      <Button color="inherit"
                              onClick={async () => {
                                await annulerInvitation(i.id); recharger();
                              }}>
                        Annuler
                      </Button>
                    </Stack>
                  </Stack>
                </Paper>
              ))}
            </Stack>
          </Box>
        )}

        {acces !== null && actifs.length === 0 && enAttente.length === 0 && (
          <Alert severity="info">
            Vous êtes seul sur ce dossier.
          </Alert>
        )}

        <Paper variant="outlined"
               sx={{ p: 3, borderStyle: 'dashed', textAlign: 'center' }}>
          <Button variant="contained"
                  onClick={() => setFormulaire({ email: '', level: 'LECTURE',
                                                 person_expertise: '' })}>
            Inviter quelqu'un
          </Button>
        </Paper>

        <Alert severity="info">
          Retirer un accès n'efface rien : la personne perd le dossier, mais
          ce qu'elle y a versé y reste, et une copie figée lui est conservée.
        </Alert>
      </Stack>

      <Dialog open={formulaire !== null} onClose={() => setFormulaire(null)}
              fullWidth maxWidth="sm">
        <DialogTitle>Inviter quelqu'un sur ce dossier</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {intervenants.length > 0 && (
              <TextField
                select label="Un intervenant déjà renseigné ?"
                value={formulaire?.person_expertise ?? ''}
                onChange={(e) => {
                  const personne = intervenants.find(
                    (p) => String(p.id) === e.target.value);
                  setFormulaire((f) => ({
                    ...f, person_expertise: e.target.value,
                    email: personne?.mail || f.email,
                  }));
                }}
                helperText="Son adresse sera reprise, s'il en a une."
              >
                <MenuItem value="">Quelqu'un d'autre</MenuItem>
                {intervenants.map((p) => (
                  <MenuItem key={p.id} value={String(p.id)}>
                    {[p.firstname, p.name].filter(Boolean).join(' ')
                      || p.name_company || 'Sans nom'}
                    {p.role1 ? ` — ${p.role1}` : ''}
                  </MenuItem>
                ))}
              </TextField>
            )}

            <TextField
              label="Son adresse électronique" type="email" required
              value={formulaire?.email ?? ''}
              onChange={(e) => setFormulaire((f) => ({ ...f, email: e.target.value }))}
              helperText="L'invitation ne s'ouvrira qu'avec un compte portant cette adresse : transférer le lien ne donne pas le dossier."
            />

            <TextField
              select label="Ce qu'il pourra faire"
              value={formulaire?.level ?? 'LECTURE'}
              onChange={(e) => setFormulaire((f) => ({ ...f, level: e.target.value }))}
              helperText={NIVEAUX.find((n) => n.code === formulaire?.level)?.aide}
            >
              {NIVEAUX.map((n) => (
                <MenuItem key={n.code} value={n.code}>{n.libelle}</MenuItem>
              ))}
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setFormulaire(null)}>Annuler</Button>
          <Button variant="contained" onClick={envoyer}
                  disabled={occupe || !formulaire?.email?.trim()}>
            Inviter
          </Button>
        </DialogActions>
      </Dialog>
    </Layout>
  );
}
